import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled, useTheme } from "@mui/system";
import React from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import ContainedButton from "src/components/common/ContainedButton";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import CryptoSelectionModalV2, {
  ICurrency,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import { TradeAction } from "src/features/trade/types";
import api from "src/store/features/api";
import { setBuyInput, setKnownSide, transerSellMaxTotal } from "src/store/features/swap.slice";
import { useUserSlice } from "src/store/features/user.slice";
import { isTokenRestrictedForCountry } from "src/utils/restrictedCountry";

export const Wrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  position: "relative",
  paddingBottom: "20px",
}));

export const ButtonText = styled("div")(() => ({
  fontSize: "16px",
}));

export const MaxText = styled("div")(() => ({
  position: "absolute",
  top: 45,
  width: "200px",
  fontSize: "14px !important",
  textAlign: "right",
  paddingRight: "10px",
  zIndex: 99,
  whiteSpace: "nowrap",
}));

interface MaxButtonProps {
  hide: boolean;
}
export const MaxButton = styled("span")<MaxButtonProps>(({ hide }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  textDecorationLine: "underline",
  paddingLeft: "5px",
  cursor: "pointer",
  display: hide ? "none" : "flex-inline",

  "&:hover": {
    color: "rgba(78, 112, 134, 1)",
  },
}));

interface TradeCoinSelectionButtonProps {
  code: string;
  logoURL: string;
  maxTotal: string;
  action: TradeAction;
}

const TradeCoinSelectionButton = ({
  code,
  logoURL,
  maxTotal,
  action,
}: TradeCoinSelectionButtonProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const buyCodeURL = searchParams.get("code");

  const {
    defaultCurrency,
    details: { customerId },
  } = useUserSlice();
  const { code: userBaseCurrency } = defaultCurrency;

  // user id ref
  const currencyUserID = React.useRef<string>(customerId);

  const { data: userData } = api.useGetUserDatav2Query();
  const userCountry = userData?.address?.country;

  // APIs
  const {
    data: userHoldings = [],
    isLoading: isHoldingsLoading,
    isFetching: isHoldingsFetching,
    isError: isHoldingsError,
    refetch: refetchWallet,
  } = api.useGetWalletPageHoldingsNewQuery({
    baseCurrency: userBaseCurrency,
  });

  const {
    data: cryptoAll = [],
    isLoading: isCryptoAllLoading,
    isError: isCryptoAllError,
  } = api.useGetCryptoAllQuery({
    quote: userBaseCurrency,
  });

  const {
    data: forexAll = [],
    isLoading: isFiatLoading,
    isError: isFiatError,
  } = api.useGetFiatCurrenciesQuery({
    quote: userBaseCurrency,
  });

  /**
   * Memoized array of user's crypto holdings filtered by permissible currencies and formatted as ICurrency objects.
   */
  const usersCryptoHoldings = React.useMemo((): ICurrency[] => {
    if (!userHoldings) return [];

    // remove user holdings that DO NOT have buy or sell permissions
    const permissiableHoldings = userHoldings.filter((currency) => {
      return currency.currencyConfig?.permissions?.sell;
    });

    // map through permissible holdings and format as ICurrency objects
    return permissiableHoldings.map((currency): ICurrency => {
      const config = currency.currencyConfig;
      return {
        userBaseCurrency: userBaseCurrency,
        code: config.code ?? "",
        displayName: config.displayName ?? "",
        logoURL: config.logoUrl ?? "",
        units: currency.available.toString(),
        value: currency.value.toString(),
        rank: "",
        sector: "",
        price: currency.price,
      };
    });
  }, [userHoldings, userBaseCurrency]);

  /**
   * Memoized array of all cryptocurrencies filtered by permissible currencies and formatted as ICurrency objects.
   */
  const cryptoCurrencies = React.useMemo((): ICurrency[] => {
    if (!cryptoAll) return [];

    // remove cryptos that DO NOT have buy or sell permissions
    const permissiableCryptos = cryptoAll.filter((currency) => {
      return currency.permissions?.buy && !isTokenRestrictedForCountry(currency, userCountry);
    });

    // map through permissible cryptos and format as ICurrency objects
    return permissiableCryptos.map((currency): ICurrency => {
      return {
        userBaseCurrency: userBaseCurrency,
        code: currency.code ?? "",
        displayName: currency.displayName ?? "",
        logoURL: currency.logoUrl ?? "",
        units: "0",
        value: "0",
        rank: "",
        sector: "",
        price: currency.price ?? 0,
      };
    });
  }, [cryptoAll, userCountry]);

  /**
   * Memoized array of all forex currencies filtered by permissible currencies and formatted as ICurrency objects.
   */
  const forexCurrencies = React.useMemo((): ICurrency[] => {
    if (!forexAll) return [];

    // remove forex that DO NOT have buy or sell permissions
    const permissiableforex = forexAll.filter((currency) => {
      return currency.permissions?.buy;
    });

    // map through permissible forex and format as ICurrency objects
    return permissiableforex.map((currency): ICurrency => {
      return {
        userBaseCurrency: userBaseCurrency,
        code: currency.code ?? "",
        displayName: currency.name ?? "",
        logoURL: currency.logoUrl ?? "",
        units: "0",
        value: "0",
        rank: "",
        sector: "",
        price: currency.price ?? 0,
      };
    });
  }, [forexAll]);

  /**
   * Memoized array of cryptocurrencies and forexes combined with user's holdings, matching by currency code.
   */
  const cryptoCurrenciesWithUserHoldings = React.useMemo((): ICurrency[] => {
    if (!cryptoCurrencies || !forexCurrencies) return [];

    // Helper function to combine currency data with user's holdings
    const combineWithUserHoldings = (currencies: ICurrency[]): ICurrency[] => {
      return currencies.map((currency): ICurrency => {
        const userCurrency = usersCryptoHoldings.find(
          (userCurrency): boolean => userCurrency.code === currency.code,
        );
        return userCurrency
          ? { ...currency, units: userCurrency.units, value: userCurrency.value }
          : currency;
      });
    };

    const combinedCryptos = combineWithUserHoldings(cryptoCurrencies);
    const combinedForexes = combineWithUserHoldings(forexCurrencies);

    return [...combinedCryptos, ...combinedForexes];
  }, [cryptoCurrencies, forexCurrencies, usersCryptoHoldings]);

  const [open, setOpen] = React.useState<boolean>(false);

  const handleOpenCryptoSelectionModal = (): void => {
    setOpen(true);
  };

  const handleCloseCryptoSelectionModal = (): void => {
    setOpen(false);
  };

  const handleMaxTotal = (): void => {
    if (action === "sell") {
      trackEvent("Button Click", { button: "Max button clicked" }, "Trade");
      dispatch(transerSellMaxTotal());
      dispatch(setKnownSide("sell"));
    }
  };

  const handleErrorSide = (): boolean => {
    if (action === "sell") return isHoldingsError;
    return isCryptoAllError || isFiatError;
  };

  const getLoadingSide = (): boolean => {
    if (action === "sell") return isHoldingsLoading || isHoldingsFetching;
    return isCryptoAllLoading || isFiatLoading;
  };

  const getCurrencyData = (): ICurrency[] => {
    if (action === "sell") return usersCryptoHoldings;
    return cryptoCurrenciesWithUserHoldings;
  };

  React.useEffect((): void => {
    if (!cryptoCurrenciesWithUserHoldings) return;
    if (buyCodeURL) {
      const currency = getCurrencyData().find(
        (currency): boolean => currency.code.toLowerCase() === buyCodeURL.toLowerCase(),
      );
      if (currency) {
        const { code, logoURL, units, price } = currency;
        if (action === "buy") dispatch(setBuyInput({ code, logoURL, maxTotal: units, price }));
      }
    }
  }, [buyCodeURL, cryptoCurrenciesWithUserHoldings]);

  React.useEffect((): void => {
    // user changed user, so refresh rtkq and fetch again
    if (currencyUserID.current !== customerId) {
      currencyUserID.current = customerId;
      refetchWallet();
    }
  }, []);

  const balanceCode = code === "Select" ? "" : code;
  // this is to adjust the width of the button based on the length of the code
  const btnWidth = code !== "Select" && code.length > 3 ? "125px" : "115px";

  const theme = useTheme();
  return (
    <Wrapper>
      <ContainedButton
        id={`trade-coin-selection-button-${action}`}
        color={theme.palette.mode === "light" ? "navy" : "dark"}
        borderRadius="25px"
        width={btnWidth}
        height="40px"
        startIcon={
          code !== "Select" ? <CountryOrCryptoFlag name={logoURL || code} size={20} /> : undefined
        }
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpenCryptoSelectionModal}>
        <ButtonText>{code}</ButtonText>
      </ContainedButton>
      <MaxText>
        Balance: {maxTotal}
        {balanceCode && (
          <>
            <span style={{ paddingLeft: "4px" }}>{balanceCode}</span>
            <MaxButton id="max-button" hide={action === "buy"} onClick={handleMaxTotal}>
              Max
            </MaxButton>
          </>
        )}
      </MaxText>

      <CryptoSelectionModalV2
        open={open}
        onClose={handleCloseCryptoSelectionModal}
        currencies={getCurrencyData()}
        hasError={handleErrorSide()}
        isLoading={getLoadingSide()}
        action={action}
      />
    </Wrapper>
  );
};

export default TradeCoinSelectionButton;
